import { Button } from "@Light/components/page/Button";
import { ButtonFooter } from "@Light/components/page/ButtonFooter";
import { Hyperlink } from "@Light/components/page/Hyperlink";
import { OverlayPortal } from "@Light/components/page/Overlay";
import { useScaffold } from "@Light/scaffold";
import { formatBrandedPlanName } from "@Light/utils/formatting";
import { getPlanLinks } from "@Light/utils/planLinks";
import type { EnrollmentTermsProps } from "@Light/utils/utility";
import { useState } from "react";
import { EnrollBottomSheet } from "../common/EnrollBottomSheet";

const SECTIONS = [
  {
    heading: "Authorization to Enroll",
    body: `I am the electricity account holder of record, the account holder’s
    spouse, or an authorized representative of this account, and I am over the
    age of 18. By submitting this enrollment request electronically, I affirm
    that I have read, understand, and accept Light Energy’s Terms and
    Conditions, Contract Summary, and Disclosure Statement, provided on the
    website in electronic form, and that I have received a copy of the PA
    Energy Consumer Bill of Rights. I voluntarily authorize Light Energy to
    take all steps necessary to provide me with my product selection,
    including getting the information it needs from my utility to begin or
    continue service. I also authorize Light Energy or its representatives, if
    necessary, to contact me or an authorized representative of the enrolled
    account(s) at the contact information I provided to complete my
    enrollment. This includes calls or texts to my cell phone or landline using
    automatic telephone dialing technology and/or pre-recorded messages, even
    if my phone number is on a national or state “do not call” list.`,
  },
  {
    heading: "Release of Utility Information",
    body: `By entering into this contract, I consent to my electric utility
    releasing my account information to Light Energy. This includes my utility
    account number, my historical and ongoing electricity usage data, and
    billing and payment information. Light Energy will use this information to
    enroll me, price and provide my electricity supply, bill me, and service my
    account.`,
  },
  {
    heading: "Electronic Delivery Acknowledgment",
    body: `I acknowledge that Light Energy will deliver my account documents,
    notices and other disclosures electronically to the email address I
    provided, unless Light Energy is required to send them by mail or in
    another format. If email delivery fails, Light Energy will try to deliver
    my account documents by mail. I agree to tell Light Energy if my email
    address changes so that my documents can be delivered.`,
  },
  {
    heading: "Privacy Policy",
    body: `By submitting my enrollment, I also agree to Light Energy’s Privacy
    Policy. It allows Light Energy to share my information with affiliates and
    marketing partners for the purposes described in the policy, including
    marketing products or services that may interest me.`,
  },
  {
    heading: "Continuous Service and Cancellation",
    body: `I understand that this is a continuous service contract. I will be
    charged monthly unless I cancel, Light Energy cancels, or I switch to
    another supplier. To cancel, I can call, email, or visit the Light Energy
    website. My utility controls the cancellation timeline, so Light Energy
    will continue as my supplier, and charge me, until a final date set by my
    utility.`,
  },
  {
    heading: "Renewal Provision/Agreement Expiration/Change in Terms",
    body: `I understand that if I have a fixed duration Agreement that will be
    ending, or whenever Light wants to change the Agreement, I will receive two
    separate notices before the Agreement ends or the changes happen. I will
    receive the first notice 45-60 days before, and the second notice 30 days
    before the expiration date or the date the change becomes effective. These
    notices will explain my options. I will not be subject to a penalty or fee
    if I terminate the Agreement at any time between the date the options
    notice is issued and the expiration of the Agreement.`,
  },
];

export const PaAuthorizationTerms = ({ plan }: EnrollmentTermsProps) => {
  const scaffold = useScaffold();
  const [open, setOpen] = useState(false);
  const disclosureStatement = getPlanLinks(plan).find(
    (link) => link.name === "Disclosure Statement",
  );

  return (
    <>
      I have read, understand, and agree to the{" "}
      {disclosureStatement ? (
        <Hyperlink
          href={disclosureStatement.url}
          target="_blank"
          data-testid="disclosure-statement-link"
        >
          Disclosure Statement
        </Hyperlink>
      ) : (
        "Disclosure Statement"
      )}{" "}
      and{" "}
      <button
        type="button"
        className="cursor-pointer underline"
        onClick={(e) => {
          // Keep the surrounding label from toggling its checkbox.
          e.preventDefault();
          setOpen(true);
        }}
        data-testid="authorization-terms-link"
      >
        Authorization to Enroll
      </button>{" "}
      for the {formatBrandedPlanName(scaffold.page, plan.name)}.
      <OverlayPortal>
        <EnrollBottomSheet
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          title=""
          data-testid="authorization-terms-sheet"
          footer={
            <ButtonFooter>
              <Button
                size="lg"
                className="w-full"
                onClick={() => {
                  setOpen(false);
                }}
                data-testid="authorization-terms-sheet-close"
              >
                Done
              </Button>
            </ButtonFooter>
          }
        >
          <div className="flex flex-col gap-4 text-sm text-tertiary">
            {SECTIONS.map(({ heading, body }) => (
              <section key={heading} className="flex flex-col gap-1">
                <h3 className="font-semibold text-primary">{heading}</h3>
                <p>{body}</p>
              </section>
            ))}
          </div>
        </EnrollBottomSheet>
      </OverlayPortal>
    </>
  );
};
